import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';
import { db } from '../utils/firebase';
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { toMillis } from '../utils/timestamps';
import { calculateCaloriesBurned } from '../utils/calories';
import logger from '../utils/logger';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

function EnergyBalance({ bmr = 0, caloriesBurned = 0, caloriesConsumed = 0, userId, currentWeight, refreshTrigger = 0 }) {
  const hasMeals = caloriesConsumed > 0;
  const hasExercise = caloriesBurned > 0;
  const showNet = hasMeals;
  const net = caloriesConsumed - bmr - caloriesBurned;

  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

  useEffect(() => {
    if (!userId || !bmr) return;

    const fetchChartData = async () => {
      setChartLoading(true);
      try {
        // Build 7-day buckets (index 0 = 6 days ago, index 6 = today)
        const now = new Date();
        const days = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
          days.push({
            label: `${d.getMonth() + 1}/${d.getDate()}`,
            start: d.getTime(),
            end: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime(),
          });
        }

        const sevenDaysAgo = Timestamp.fromMillis(days[0].start);

        const [mealsSnap, repsSnap] = await Promise.all([
          getDocs(query(
            collection(db, 'users', userId, 'meals'),
            where('timestamp', '>=', sevenDaysAgo),
            orderBy('timestamp', 'asc')
          )),
          getDocs(query(
            collection(db, 'users', userId, 'reps'),
            where('timestamp', '>=', sevenDaysAgo),
            orderBy('timestamp', 'asc')
          )),
        ]);

        const consumed = new Array(7).fill(0);
        const burned = new Array(7).fill(0);
        const hasData = new Array(7).fill(false);

        for (const d of mealsSnap.docs) {
          const ms = toMillis(d.data().timestamp);
          for (let i = 0; i < 7; i++) {
            if (ms >= days[i].start && ms <= days[i].end) {
              consumed[i] += d.data().calories || 0;
              hasData[i] = true;
              break;
            }
          }
        }

        for (const d of repsSnap.docs) {
          const data = d.data();
          const ms = toMillis(data.timestamp);
          for (let i = 0; i < 7; i++) {
            if (ms >= days[i].start && ms <= days[i].end) {
              burned[i] += calculateCaloriesBurned(data, currentWeight);
              hasData[i] = true;
              break;
            }
          }
        }

        setChartData(days.map((day, i) => ({
          label: day.label,
          net: hasData[i] ? Math.round(consumed[i] - bmr - burned[i]) : null,
        })));
      } catch (error) {
        logger.error(error, { page: 'User', action: 'fetchEnergyChartData' });
      } finally {
        setChartLoading(false);
      }
    };

    fetchChartData();
  }, [userId, bmr, currentWeight, refreshTrigger]);

  // Chart config
  const barColors = chartData.map(d =>
    d.net === null ? '#475569'
    : d.net > 0 ? 'rgba(249, 115, 22, 0.85)'
    : d.net < 0 ? 'rgba(34, 197, 94, 0.85)'
    : '#475569'
  );

  const barValues = chartData.map(d => d.net === null ? 0 : d.net);
  const lowestValue = Math.min(0, ...barValues);
  const highestValue = Math.max(0, ...barValues);

  const chart = {
    labels: chartData.map(d => d.label),
    datasets: [{
      data: barValues,
      backgroundColor: barColors,
      borderRadius: 3,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0f172a',
        titleColor: '#f1f5f9',
        bodyColor: '#f1f5f9',
        borderColor: '#334155',
        borderWidth: 1,
        callbacks: {
          label: (ctx) => {
            const d = chartData[ctx.dataIndex];
            if (d.net === null) return 'No data logged';
            const label = d.net > 0 ? 'surplus' : d.net < 0 ? 'deficit' : '';
            return `${d.net} cal${label ? ' ' + label : ''}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: '#94a3b8', font: { size: 11 } },
        grid: { display: false },
      },
      y: {
        min: Math.min(-300, lowestValue),
        max: Math.max(300, highestValue),
        ticks: { color: '#94a3b8', font: { size: 10 } },
        grid: {
          color: (context) => {
            if (context.tick.value === 0) return '#f97316';
            return '#334155';
          },
          lineWidth: (context) => {
            if (context.tick.value === 0) return 3;
            return 1;
          },
        },
      },
    },
  };

  return (
    <div className="weight-tracking">
      <h3>Today's Energy Balance</h3>
      <table className="energy-balance-table">
        <tbody>
          {hasMeals && (
            <tr>
              <td className="eb-label">Food consumed</td>
              <td className="eb-value">+{Math.round(caloriesConsumed)} cal</td>
            </tr>
          )}
          <tr>
            <td className="eb-label">Base (BMR)</td>
            <td className="eb-value">&minus;{Math.round(bmr)} cal</td>
          </tr>
          {hasMeals && (
            <tr>
              <td className="eb-label">Exercise burned</td>
              <td className="eb-value">&minus;{Math.round(caloriesBurned)} cal</td>
            </tr>
          )}
          {showNet && (
            <>
              <tr>
                <td colSpan={2}><hr className="eb-divider" /></td>
              </tr>
              <tr className="eb-net-row">
                <td className="eb-label">Net</td>
                <td className="eb-value">
                  {Math.round(net)} cal{' '}
                  {net > 0 && <span className="eb-surplus">surplus</span>}
                  {net < 0 && <span className="eb-deficit">deficit</span>}
                </td>
              </tr>
              {!hasExercise && (
                <tr>
                  <td colSpan={2}>
                    <p className="eb-muted" style={{ fontStyle: 'italic', margin: '4px 0 0', whiteSpace: 'nowrap' }}>Log a workout to lower your net.</p>
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
      {!hasMeals && !hasExercise && (
        <p className="eb-muted">Log meals and exercise to see your full balance.</p>
      )}
      {!hasMeals && hasExercise && (
        <p className="eb-muted">Log meals to see your daily balance.</p>
      )}

      <hr className="eb-divider" style={{ margin: '14px 0 8px' }} />
      <p className="eb-muted" style={{ fontStyle: 'normal', fontWeight: 600, marginBottom: '6px' }}>7-day trend</p>
      {chartLoading ? (
        <p className="eb-muted">Loading chart...</p>
      ) : (
        <div style={{ height: '160px' }}>
          <Bar data={chart} options={chartOptions} />
        </div>
      )}
    </div>
  );
}

export default EnergyBalance;
